import toast from 'react-hot-toast';
import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  avatar?: string;
  coverPhoto?: string;
  position?: string;
  bio?: string;
  chatStatus?: string;
  birthDate?: string;
  address?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  contractType?: string;
  contractStart?: string;
  contractEnd?: string;
  baseSalary?: number;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ token?: string; user?: User; error?: string }>;
  logout: () => void;
  setUser: (user: User | null) => void;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(localStorage.getItem('platform_token'));
  const [user, setUser] = useState<User | null>(() => {
    const s = localStorage.getItem('platform_user');
    return s ? JSON.parse(s) : null;
  });
  const [loading, setLoading] = useState(true);

  
  // --- MOTOR DE CIBERSEGURIDAD: CIERRE DE SESIÓN TRAS 4 HORAS DE INACTIVIDAD ---
  const FOUR_HOURS_MS = 4 * 60 * 60 * 1000; // 4 horas exactas (14.400.000 ms)

  const updateLastActivity = () => {
    try {
      localStorage.setItem('zoik_last_activity', Date.now().toString());
    } catch {}
  };

  useEffect(() => {
    if (!user) return;

    // Registrar actividad inicial
    updateLastActivity();

    // Eventos que detectan actividad real del usuario
    const activityEvents = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll', 'click'];
    
    // Throttle para no sobrecargar el almacenamiento en cada micro-movimiento
    let lastThrottledTime = Date.now();
    const handleUserActivity = () => {
      const now = Date.now();
      if (now - lastThrottledTime > 30000) { // Actualizar cada 30 segundos de actividad
        lastThrottledTime = now;
        updateLastActivity();
      }
    };

    activityEvents.forEach(evt => window.addEventListener(evt, handleUserActivity, { passive: true }));

    // Heartbeat cada 60 segundos que comprueba si pasaron 4 horas de inactividad
    const inactivityInterval = setInterval(() => {
      const lastActivityStr = localStorage.getItem('zoik_last_activity');
      if (lastActivityStr) {
        const lastActivity = parseInt(lastActivityStr, 10);
        const elapsed = Date.now() - lastActivity;

        if (elapsed >= FOUR_HOURS_MS) {
          toast.error('🔒 Tu sesión ha expirado por 4 horas de inactividad por seguridad.', {
            duration: 8000,
            icon: '🛡️'
          });
          logout();
        }
      }
    }, 60000);

    return () => {
      activityEvents.forEach(evt => window.removeEventListener(evt, handleUserActivity));
      clearInterval(inactivityInterval);
    };
  }, [user]);


  const logout = () => {
    localStorage.removeItem('platform_token');
    localStorage.removeItem('platform_user');
    setToken(null);
    setUser(null);
    window.location.href = '/login';
  };

  const login = async (email: string, password: string) => {
    const r = await api.post('/auth/login', { email, password });
    if (r.data && r.data.token) {
      localStorage.setItem('platform_token', r.data.token);
      localStorage.setItem('platform_user', JSON.stringify(r.data.user));
      setToken(r.data.token);
      setUser(r.data.user);
    }
    return r.data;
  };

  useEffect(() => {
    const checkAuth = async () => {
      const savedToken = localStorage.getItem('platform_token');
      if (!savedToken) {
        setLoading(false);
        return;
      }
      try {
        const res = await api.get('/auth/me');
        setUser(res.data);
        localStorage.setItem('platform_user', JSON.stringify(res.data));
      } catch (err) {
        localStorage.removeItem('platform_token');
        localStorage.removeItem('platform_user');
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, [token]);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
